import type { LabelFieldKey } from './labelLayoutEngine';

export type LabelCanvasShape =
  | 'rect'
  | 'ellipse'
  | 'pill'
  | 'semicircle-top'
  | 'semicircle-bottom'
  | 'triangle-up'
  | 'triangle-down'
  | 'diamond'
  | 'band-horizontal'
  | 'band-vertical';

export type LabelCanvasElementType =
  | 'text'
  | 'boundField'
  | 'image'
  | 'shape'
  | 'barcode'
  | 'panel'
  | 'nutritionTable';

export type NutritionTableRow = {
  label: string;
  value: string;
};

export const DEFAULT_NUTRITION_ROWS: NutritionTableRow[] = [
  { label: 'Calories', value: '120' },
  { label: 'Fat', value: '2 g' },
  { label: 'Sodium', value: '15 mg' },
  { label: 'Total Carbohydrate', value: '28 g' },
  { label: 'Protein', value: '3 g' },
];

export type LabelCanvasElement = {
  id: string;
  type: LabelCanvasElementType;
  /** Position & size as % of label (0–100) */
  x: number;
  y: number;
  w: number;
  h: number;
  zIndex: number;
  rotation?: number;
  locked?: boolean;
  /** Text & typography */
  text?: string;
  fontSizePct?: number;
  fontWeight?: number | string;
  fontFamily?: string;
  fontStyle?: 'normal' | 'italic';
  textAlign?: 'left' | 'center' | 'right';
  color?: string;
  letterSpacing?: number;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  lineHeight?: number;
  /** Shape / panel */
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  shape?: LabelCanvasShape;
  opacity?: number;
  /** Image */
  imageUrl?: string;
  objectFit?: 'cover' | 'contain' | 'fill';
  /** Data binding */
  fieldKey?: LabelFieldKey;
  panelTitle?: string;
  nutritionRows?: NutritionTableRow[];
  nutritionServingNote?: string;
};

export function newCanvasElementId(): string {
  return `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function mergeCanvasElements(raw?: LabelCanvasElement[] | null): LabelCanvasElement[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((el) => el && typeof el.id === 'string')
    .map((el) => ({
      ...el,
      x: clampPct(el.x),
      y: clampPct(el.y),
      w: clampPct(el.w, 4, 100),
      h: clampPct(el.h, 2, 100),
      zIndex: Number.isFinite(el.zIndex) ? el.zIndex : 0,
    }))
    .sort((a, b) => a.zIndex - b.zIndex);
}

function clampPct(n: number, min = 0, max = 100): number {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}

export function createCanvasElement(
  type: LabelCanvasElementType,
  partial?: Partial<LabelCanvasElement>,
): LabelCanvasElement {
  const base: LabelCanvasElement = {
    id: newCanvasElementId(),
    type,
    x: 10,
    y: 10,
    w: 30,
    h: 12,
    zIndex: 1,
    fontSizePct: 3.5,
    fontWeight: 600,
    fontFamily: 'system-ui, sans-serif',
    textAlign: 'left',
    color: '#0f172a',
    backgroundColor: 'transparent',
    borderColor: '#cbd5e1',
    borderWidth: 0,
    borderRadius: 0,
    shape: 'rect',
    opacity: 1,
    objectFit: 'cover',
    textTransform: 'none',
    lineHeight: 1.2,
  };

  switch (type) {
    case 'text':
      return { ...base, text: 'Your text', w: 40, h: 10, ...partial };
    case 'boundField':
      return {
        ...base,
        type: 'boundField',
        fieldKey: 'name',
        w: 50,
        h: 12,
        fontWeight: 700,
        fontSizePct: 4.5,
        ...partial,
      };
    case 'image':
      return { ...base, type: 'image', w: 28, h: 35, ...partial };
    case 'shape':
      return {
        ...base,
        type: 'shape',
        w: 100,
        h: 100,
        x: 0,
        y: 0,
        zIndex: 0,
        backgroundColor: '#f97316',
        ...partial,
      };
    case 'barcode':
      return { ...base, type: 'barcode', w: 35, h: 14, zIndex: 5, ...partial };
    case 'panel':
      return {
        ...base,
        type: 'panel',
        w: 38,
        h: 55,
        x: 58,
        y: 8,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderColor: '#ffffff',
        borderRadius: 4,
        panelTitle: 'INGREDIENTS',
        text: 'Add panel content…',
        fontSizePct: 2.2,
        color: '#ffffff',
        ...partial,
      };
    case 'nutritionTable':
      return {
        ...base,
        type: 'nutritionTable',
        w: 32,
        h: 42,
        x: 62,
        y: 22,
        panelTitle: 'Nutrition Facts',
        nutritionServingNote: 'Per serving',
        nutritionRows: DEFAULT_NUTRITION_ROWS.map((r) => ({ ...r })),
        borderWidth: 2,
        borderColor: '#0f172a',
        backgroundColor: '#ffffff',
        color: '#0f172a',
        fontSizePct: 2,
        ...partial,
      };
    default:
      return { ...base, ...partial };
  }
}

export function nextZIndex(elements: LabelCanvasElement[]): number {
  if (elements.length === 0) return 1;
  return Math.max(...elements.map((e) => e.zIndex)) + 1;
}

export function moveElement(
  elements: LabelCanvasElement[],
  id: string,
  patch: Partial<Pick<LabelCanvasElement, 'x' | 'y' | 'w' | 'h' | 'zIndex'>>,
): LabelCanvasElement[] {
  return elements.map((el) => (el.id === id ? { ...el, ...patch } : el));
}

export function updateElement(
  elements: LabelCanvasElement[],
  id: string,
  patch: Partial<LabelCanvasElement>,
): LabelCanvasElement[] {
  return elements.map((el) => (el.id === id ? { ...el, ...patch } : el));
}

export function removeElement(elements: LabelCanvasElement[], id: string): LabelCanvasElement[] {
  return elements.filter((el) => el.id !== id);
}

export function duplicateElement(elements: LabelCanvasElement[], id: string): LabelCanvasElement[] {
  const src = elements.find((e) => e.id === id);
  if (!src) return elements;
  const copy: LabelCanvasElement = {
    ...src,
    id: newCanvasElementId(),
    x: clampPct(src.x + 2),
    y: clampPct(src.y + 2),
    zIndex: nextZIndex(elements),
  };
  return [...elements, copy];
}

export function reorderLayer(
  elements: LabelCanvasElement[],
  id: string,
  direction: 'up' | 'down' | 'front' | 'back',
): LabelCanvasElement[] {
  const sorted = [...elements].sort((a, b) => a.zIndex - b.zIndex);
  const idx = sorted.findIndex((e) => e.id === id);
  if (idx < 0) return elements;

  if (direction === 'front') {
    const maxZ = Math.max(...sorted.map((e) => e.zIndex));
    return updateElement(elements, id, { zIndex: maxZ + 1 });
  }
  if (direction === 'back') {
    const minZ = Math.min(...sorted.map((e) => e.zIndex));
    return updateElement(elements, id, { zIndex: minZ - 1 });
  }

  const swapIdx = direction === 'up' ? idx + 1 : idx - 1;
  if (swapIdx < 0 || swapIdx >= sorted.length) return elements;
  const a = sorted[idx];
  const b = sorted[swapIdx];
  return elements.map((el) => {
    if (el.id === a.id) return { ...el, zIndex: b.zIndex };
    if (el.id === b.id) return { ...el, zIndex: a.zIndex };
    return el;
  });
}
